import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  editAdminUnitsEmployeeAction,
  editAdminUnitsAction,
  createAdminUnitsAction,
  createTransferBill,
  editTransferBill,
} from "redux/actions/adminUnits/adminUnitsActions";
import { initialValues, billingValidations } from "./initialValues";
import { useSearch } from "hooks/useSearch";
import { deliveryTypeOpt, routesTypes, fuelTypeOpt } from "helpers/dictionary";
import { formatNumber } from "helpers/getNumberFormats";
import { useForm } from "hooks/useForm";
import { validateObject } from "helpers/validateObject";
import { PERMISSIONS } from "router/permissions";
import { useRuleValidator } from "hooks/useRuleValidator";

const { CATALOG_UNITS_EDIT_TIPO, CATALOG_UNITS_EDIT_CAPACITY } = PERMISSIONS;

const getPriceType = (typeValue, { ESTACION, ESTACIONARIO, MAYORISTA_E, PORTATIL }) => {
  const isStationary = [ESTACION, ESTACIONARIO, MAYORISTA_E].some(t => typeValue?.includes(t));
  return isStationary ? ESTACIONARIO : PORTATIL;
};

const buildUnitForm = ({ values, edit, nType, data }) => {
  const capacityValue = parseFloat(values.capacity);
  const form = {
    creLicense: values.creLicense.label?.trim(),
    idealPercentage: parseFloat(values.idealPercentage),
    flowMeter: parseFloat(values.flowMeter),
    unitPlates: values.unitPlates?.trim(),
    brand: values.brand.trim(),
    subModel: values.subModel.trim(),
    serieNumber: values.serieNumber.trim(),
    type: values.type?.value ?? data.type,
    priceType: nType,
    deliveryType: values.deliveryType?.value ?? data.deliveryType,
    fuel: values.fuel?.value ?? data.fuel,
    capacity: Number.isNaN(capacityValue) ? data.capacity : capacityValue,
  };

  if (edit) return form;

  return {
    ...form,
    idStation: values.idStation.value,
    number: `${values.ecoPart1}-${values.ecoPart2}-${values.ecoPart3}`,
  };
};

export const useAdminUnitForm = (
  data = {},
  edit = false,
  setOpen = () => { },
) => {
  const dispatch = useDispatch();
  const { stationsList } = useSelector(({ companies }) => companies);
  const { station } = useSelector(({ adminUnits }) => adminUnits);
  const { unitTypes, unitTypesObj } = useSelector(({ unitsReducer }) => unitsReducer);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const { creLicenses } = useSelector(({ globalInvoices }) => globalInvoices);

  const canEditCapacity = useRuleValidator(CATALOG_UNITS_EDIT_CAPACITY);
  const canEditTypes = useRuleValidator(CATALOG_UNITS_EDIT_TIPO);
  const capacityFieldDisabled = edit && !canEditCapacity;
  const typeFieldDisabled = edit && !canEditTypes;

  const {
    values,
    onChange,
    setValues,
    errors,
    validate,
    onChangeNumberV2,
  } = useForm(initialValues.values, initialValues.required);

  const {
    ecoPart1,
    ecoPart2,
    ecoPart3,
    type,
    idealPercentage,
  } = values;

  const {
    ESTACIONARIO,
    PORTATIL,
    ESTACION,
    MAYORISTA_E,
  } = validateObject(unitTypesObj) && unitTypesObj;
  const handleUnitCode = useCallback(
    (key, value) => {
      setValues({ [key]: value.slice(0, 3) });
    },
    [ecoPart1, ecoPart2, ecoPart3],
  );

  const handleSubmit = () => {
    const editExclusions = ["number", "idStation", "ecoPart1", "ecoPart2", "ecoPart3"];
    if (typeFieldDisabled) editExclusions.push("type", "deliveryType", "fuel");
    if (capacityFieldDisabled) editExclusions.push("capacity");

    validate(edit ? editExclusions : [], () => {
      const typeValue = type?.value ?? data.type;
      const nType = typeFieldDisabled
        ? data.priceType
        : getPriceType(typeValue, { ESTACION, ESTACIONARIO, MAYORISTA_E, PORTATIL });
      const form = buildUnitForm({ values, edit, nType, data });

      if (edit) {
        dispatch(editAdminUnitsAction(data.id, form, setOpen));
      } else {
        dispatch(createAdminUnitsAction(form, setOpen, station?.id));
      }
    });
  };

  useEffect(() => {
    if (ecoPart1.length > 3) handleUnitCode("ecoPart1", ecoPart1);
    if (ecoPart2.length > 3) handleUnitCode("ecoPart2", ecoPart2);
    if (ecoPart3.length > 3) handleUnitCode("ecoPart3", ecoPart3);
  }, [ecoPart1, ecoPart2, ecoPart3]);

  useEffect(() => {
    edit
      ? setValues({
        idStation: station,
        number: data.number || "",
        type: unitTypes.find((item) => item.value === data.type) || null,
        deliveryType: deliveryTypeOpt.find(
          (item) => item.value === data.deliveryType,
        ) || null,
        fuel: fuelTypeOpt.find((item) => item.value === data.fuel) || null,
        capacity: data.capacity,
        capacity_: formatNumber({ input: data.capacity }),
        idealPercentage: data.idealPercentage,
        idealPercentage_: data.idealPercentage != null ? String(data.idealPercentage) : "",
        creLicense:
          creLicenses.find((item) =>
            item.creLicense.includes(data.creLicense),
          ) || null,
        unitPlates: data.unitPlates || "",
        flowMeter: data.flowMeter,
        flowMeter_: formatNumber({ input: data.flowMeter }),
        brand: data.brand || "",
        subModel: data.subModel || "",
        serieNumber: data.serieNumber || "",
      })
      : setValues({ idStation: station });
  }, []);

  useEffect(() => {
    idealPercentage > 100 &&
      setValues({ idealPercentage: 100, idealPercentage_: "100" });
    idealPercentage < 0 &&
      setValues({ idealPercentage: 0, idealPercentage_: "0" });
  }, [idealPercentage]);

  return {
    values,
    setValues,
    handleSubmit,
    isLoadingComp,
    stationsList,
    unitTypes,
    creLicenses,
    errors,
    validate,
    onChangeNumberV2,
    onChange,
    deliveryTypeOpt,
    fuelTypeOpt,
    routesTypes,
    capacityFieldDisabled,
    typeFieldDisabled,
  };
};

export const useAddUnitEmployeeForm = (data = {}, setOpen = () => { }) => {
  const dispatch = useDispatch();
  const { isLoadingComp } = useSelector(({ search }) => search);

  const { employeesArr } = useSelector(
    ({ employeesCatalog }) => employeesCatalog,
  );
  const state = useSelector(({ adminUnits }) => adminUnits);
  const { station } = state;
  const { setSnackbar } = useSearch();
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [initialEmployees, setInitialEmployees] = useState({});
  const { values, setValues } = useForm(initialValues.employeesValues);

  const { idEmployee, idHelper, idSecondHelper } = values;

  const hasChanges =
    idEmployee?.id !== initialEmployees.idEmployee ||
    idHelper?.id !== initialEmployees.idHelper ||
    idSecondHelper?.id !== initialEmployees.idSecondHelper;

  const handleDriver = (v) => {
    if (!v) return setValues({ idEmployee: null });
    if (v?.id === idHelper?.id || v?.id === idSecondHelper?.id) {
      setSnackbar("Ya se encuentra asignado ese empleado", "warning");
      return setValues({ idEmployee: idEmployee });
    }
    setValues({ idEmployee: v });
  };

  const handleHelper = (v) => {
    if (!v) return setValues({ idHelper: null });
    if (v?.id === idEmployee?.id || v?.id === idSecondHelper?.id) {
      setSnackbar("Ya se encuentra asignado ese empleado", "warning");
      return setValues({ idHelper: idHelper });
    }
    setValues({ idHelper: v });
  };

  const handleSecondHelper = (v) => {
    if (!v) return setValues({ idSecondHelper: null });
    if (v?.id === idEmployee?.id || v?.id === idHelper?.id) {
      setSnackbar("Ya se encuentra asignado ese empleado", "warning");
      return setValues({ idSecondHelper: idSecondHelper });
    }
    setValues({ idSecondHelper: v });
  };

  const handleSubmit = () => {

    if (!idEmployee)
      return setSnackbar("Es necesario asignar un operador", "warning");
    const form = {
      idDriver: idEmployee?.id,
      idHelper: idHelper?.id || null,
      idSecondHelper: idSecondHelper?.id || null
    };
    dispatch(editAdminUnitsEmployeeAction(data?.id, form, setOpen));
  };

  useEffect(() => {
    if (station?.id) {
      const active = employeesArr
        .filter((emp) => emp.status === 1)
        .map((emp) => ({
          ...emp,
          label: `${emp.name} ${emp.lastName} ${emp.secondName} - ${emp.number}`,
        }));
      setActiveEmployees(active);
      const foundDriver = active.find((emp) => emp?.id === data.idEmployee) || null;
      const foundHelper = active.find((emp) => emp?.id === data.idHelper) || null;
      const foundSecondHelper = active.find((emp) => emp?.id === data.idSecondHelper) || null;
      setValues({
        idEmployee: foundDriver,
        idHelper: foundHelper,
        idSecondHelper: foundSecondHelper,
      });
      setInitialEmployees({
        idEmployee: foundDriver?.id || null,
        idHelper: foundHelper?.id || null,
        idSecondHelper: foundSecondHelper?.id || null,
      });
    }
  }, []);

  return {
    activeEmployees,
    values,
    setValues,
    handleSubmit,
    handleDriver,
    handleHelper,
    handleSecondHelper,
    isLoadingComp,
    hasChanges,
  };
};

export const useBillingForm = (data = {}, setOpen = () => {}) => {
  const dispatch = useDispatch();
  const { stationsList } = useSelector(({ companies }) => companies);
  const { station } = useSelector(({ adminUnits }) => adminUnits);
  const { creLicenses } = useSelector(({ globalInvoices }) => globalInvoices);
  const { isLoadingComp } = useSelector(({ search }) => search);

  const licenseOptions = useMemo(
    () => creLicenses?.filter((lic) => lic.id === station?.id),
    [creLicenses, station?.id],
  );
  const policyVal = data.unitBilling?.insurancePolicyValue;
  const weightVal = data.unitBilling?.unitWeight;
  const distanceVal = data.unitBilling?.kmEstimation;
  const initialValues = {
    selectedStation: station,
    selectedCreLicense: licenseOptions?.find((lic) => lic.creLicense.includes(data.creLicense)) || null,
    unitPlates: data.unitPlates || "",
    insurance: data.unitBilling?.insurance || "",
    insurancePolicy: data.unitBilling?.insurancePolicy || "",
    insurancePolicyValue: String(policyVal ?? "0"),
    insurancePolicyValue_: formatNumber({ input: policyVal }),
    unitModel: data.unitBilling?.unitModel || "",
    unitWeight: String(weightVal ?? "0"),
    unitWeight_: formatNumber({ input: weightVal }),
    kmEstimation: String(distanceVal ?? "0"),
    kmEstimation_: formatNumber({ input: distanceVal }),
    vehicleConfig: data.unitBilling?.vehicleConfig || "C2",
  };

  const {
    values,
    onChange,
    setValues,
    errors,
    onChangeNumberV2,
    validate,
  } = useForm(initialValues, billingValidations);

  const {
    selectedCreLicense,
    unitPlates,
    insurance,
    insurancePolicy,
    insurancePolicyValue,
    unitModel,
    unitWeight,
    kmEstimation,
    vehicleConfig,
  } = values;

  const handleValidatedChange = useCallback((validator) => (e) => {
    const { value } = e.target;
    if (!validator(value)) return;
    onChange(e);
  }, [onChange]);

  const handleSubmit = async () => {
    validate([], async () => {
      const form = {
        idUnit: data.id,
        idCree: selectedCreLicense.id,
        creLicense: selectedCreLicense.creLicense,
        unitPlates,
        insurance,
        insurancePolicy,
        insurancePolicyValue: Number(insurancePolicyValue),
        unitModel,
        unitWeight: Number(unitWeight),
        kmEstimation: Number(kmEstimation),
        vehicleConfig,
      };
      const onSuccess = await (data.unitBilling?.id
        ? dispatch(editTransferBill({ billingId: data.unitBilling.id, form }))
        : dispatch(createTransferBill({ unitId: data.id, form })));

      if (onSuccess) setOpen();
    });
  };

  return {
    values,
    onChange,
    setValues,
    errors,
    onChangeNumberV2,
    handleValidatedChange,
    handleSubmit,
    isLoadingComp,
    stationsList,
    licenseOptions,
  };
};
